<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{
          facilityType.title("ＱＲコード発行単位登録")
        }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          class="mx-2"
          color="primary"
          :href="qrCodeFormPath"
          :download="qrCodeFormName"
          >入力用フォームダウンロード</v-btn
        >
      </v-toolbar>
      <v-container fluid>
        <error-message
          :error-object="errorObject"
          :error-screen-id="errorScreenId"
        />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0">
          QRコードを管理するグループの名称を入力してください。
        </p>
        <school-qr-code-guide v-if="facilityType.isSchool" />
        <facility-qr-code-guide v-else />
        <v-row>
          <v-col cols="6" md="3">
            <v-select
              v-model="municipality"
              :items="municipalities"
              item-text="municip_name"
              item-value="municip_code"
              :rules="[rules.required]"
              label="市町村"
              :placeholder="config.PLACEHOLDER_SELECT"
              clearable
              outlined
              return-object
              @change="changeMunicipality"
            >
            </v-select>
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="area"
              :items="areas"
              item-text="area_name"
              item-value="area_code"
              :rules="[rules.required]"
              label="地域（地区）"
              :placeholder="config.PLACEHOLDER_SELECT"
              clearable
              outlined
              return-object
              @change="changeArea"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="10" v-if="!facilityType.isSchool">
            <div class="sub-guide">
              「避難行動要支援者名簿」として登録する場合は、<br />
              施設区分に「避難行動要支援者名簿」を選択し、施設名は「－」を選択してください。
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" md="3" v-if="!facilityType.isSchool">
            <v-select
              v-model="type"
              :items="selectTypes"
              :rules="[rules.required]"
              label="施設区分"
              :placeholder="config.PLACEHOLDER_SELECT"
              clearable
              outlined
              @change="changeType"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-select
              v-model="facility"
              :items="facilities"
              :rules="[rules.required]"
              label="施設名"
              item-text="facility_name"
              item-value="facility_id"
              :placeholder="config.PLACEHOLDER_SELECT"
              clearable
              outlined
              return-object
              @change="changeFacility"
            >
            </v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="4" lg="3" v-if="facilityType.isSchool">
            <v-text-field
              v-model="year"
              :rules="[rules.required, rules.numberCheck, rules.yearCheck]"
              label="年度"
              type="text"
              :placeholder="config.PLACEHOLDER_INPUT"
              counter="4"
              maxlength="4"
              outlined
              clearable
              @input="registerValid"
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" v-if="!facilityType.isSchool">
            <div class="sub-guide">
              すでに登録した発行単位の名称は使用できません。<br />
              日付、施設名を入力するなどして、一意な名称にしてください。<br />
              記入例：２０２１年０７月２２日ｘｘｘ施設
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" v-if="facilityType.isSchool">
            <div class="sub-guide">
              すでに登録した発行単位の名称は使用できません。<br />
              学校名と年度と学年を入力するなどして、一意な名称にしてください。<br />
              記入例：ｘｘｘ学校２０２１年度６年１組
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-text-field
              v-model="group_name"
              :rules="[rules.required, rules.zenkaku]"
              label="発行単位"
              :placeholder="config.PLACEHOLDER_INPUT"
              counter="30"
              maxlength="30"
              outlined
              clearable
              @input="registerValid"
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              v-if="!screen.readOnly"
              :disabled="confirmFlag"
              class="mx-2"
              color="primary"
              @click="executeConfirm"
              >新規登録</v-btn
            >
          </v-col>
        </v-row>
        <v-divider class="mb-1"></v-divider>
      </v-container>
      <v-container fluid>
        <p style="margin: 5px 0 15px 0">
          QRコード発行単位は下の表を参照してください。
        </p>
        <v-divider class="mb-1"></v-divider>
      </v-container>
      <top-operation-data-table
        :headers="headers"
        :items="items"
        :sort-by="['modified_date']"
        :sort-desc="true"
        hide-default-footer
        locale="ja-jp"
        loading-text="読込中"
        no-data-text="データがありません。"
        must-sort
        class="fixedWidth"
        disp-mode="nowrap"
      >
        <template v-slot:[`item.modified_date`]="{ item }">
          {{ formatDate(item.modified_date) }}
        </template>
        <template v-slot:[`item.created_date`]="{ item }">
          {{ formatDate(item.created_date) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            small
            class="mx-2"
            color="primary"
            @click="openDialogEdit(item)"
            :disabled="!hasUpdateAuthorityByItem(item)"
          >
            編集
          </v-btn>
          <v-btn
            small
            class="mx-2"
            color="primary"
            @click="openDelete(item)"
            :disabled="!hasDeleteAuthorityByItem(item)"
          >
            削除
          </v-btn>
        </template>
        <template v-slot:[`item.qrCodeActions`]="{ item }">
          <v-btn small class="mx-2" color="primary" @click="qrCodeView(item)">
            表示
          </v-btn>
        </template>
        <template v-slot:[`item.viewActions`]="{ item }">
          <v-btn
            :disabled="item.fileup_flg === '0'"
            small
            class="mx-2"
            color="primary"
            @click="convertCSVtoFaItem(item)"
          >
            表示
          </v-btn>
        </template>
        <template v-slot:[`item.fileActions`]="{ item }">
          <v-btn
            :disabled="
              item.fileup_flg === '1' || !hasUpdateAuthorityByItem(item)
            "
            small
            class="mx-2"
            color="primary"
            @click="register(item)"
          >
            アップロード
          </v-btn>
        </template>
      </top-operation-data-table>
      <v-dialog v-model="dialogDelete" persistent max-width="400">
        <v-card>
          <v-card-title></v-card-title>
          <v-card-text>発行単位を削除してもよろしいですか？ </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="mx-2" color="primary" @click="openDeleteConfirm">
              削除
            </v-btn>
            <v-btn class="mx-2" color="primary" @click="closeDelete">
              キャンセル
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogDeleteConfirm" persistent max-width="570">
        <v-card>
          <v-card-title></v-card-title>
          <v-card-text v-if="facilityType.isSchool"
            >学校ＱＲコード発行単位を削除すると、<br />既に発行、または配布した学校ＱＲコードが全て利用できなくなります。<br />本当に削除してよろしいですか？
          </v-card-text>
          <v-card-text v-else
            >要配慮者施設ＱＲコード発行単位を削除すると、<br />既に発行、または配布した要配慮者施設ＱＲコードが全て利用できなくなります。<br />本当に削除してよろしいですか？
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="mx-2" color="primary" @click="deleteItem">
              削除
            </v-btn>
            <v-btn class="mx-2" color="primary" @click="closeDeleteConfirm">
              キャンセル
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogEdit" persistent>
        <v-card>
          <v-card-title>
            <span class="headline">発行単位の編集</span>
          </v-card-title>
          <v-card-text>
            <v-form ref="form">
              <v-container fluid>
                <v-row>
                  <v-col cols="12" sm="6" md="4" lg="3">
                    <v-text-field
                      v-model="editedItem.new_grp_name"
                      :rules="[rules.required, rules.zenkaku]"
                      label="発行単位"
                      :placeholder="config.PLACEHOLDER_INPUT"
                      counter="30"
                      maxlength="30"
                      outlined
                      @input="dialogValidation"
                      clearable
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="dialogConfirmFlag"
              class="mx-2"
              color="primary"
              @click="editGrpName"
            >
              更新
            </v-btn>
            <v-btn class="mx-2" color="primary" @click="closeDialogEdit">
              キャンセル
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-sheet>
    <ErrorDialog ref="errorDialog" />
    <v-overlay :value="overlay" opacity="1" color="white" z-index="2147483647">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>
<script>
import Vue from "vue";
import DatePicker from "@/components/DatePicker";
import ErrorDialog from "@/components/ErrorDialog";
import QrCodeFacilityType from "@/plugins/qr-code-facility-type";
import { ScreenCrud } from "@/js/value-objects/screen-crud";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import SchoolQrCodeGuide from "@/components/qr-code/SchoolQrCodeGuide";
import FacilityQrCodeGuide from "@/components/qr-code/FacilityQrCodeGuide";
import { createFacilityQrCodePdf } from "@/js/repository/pdf-creator/facilityQrCodePdfCreator";
import { createSchoolQrCodePdf } from "@/js/repository/pdf-creator/schoolQrCodePdfCreator";
import messageUtils from "@/mixins/message-utils";
import { ErrorTodoMessages } from "@/js/constants/error-messages";
import config from "@/js/const";

Vue.use(QrCodeFacilityType);
const facilityRepository = RepositoryFactory.get("facilities");
const vulFacilityEvagrpRepository = RepositoryFactory.get("vulFacilityEvagrp");
const allAreaRepository = RepositoryFactory.get("allArea");
const considerationPersonInfoRepository = RepositoryFactory.get(
  "considerationPersonInfo"
);
const studentInfoRepository = RepositoryFactory.get("studentInfo");

Vue.use(DatePicker);

export default {
  mixins: [messageUtils],
  components: {
    SchoolQrCodeGuide,
    FacilityQrCodeGuide,
    ErrorDialog,
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    TopOperationDataTable: () => import("@/components/ui/TopOperationDataTable")
  },
  data: () => ({
    loading: false,
    overlay: false,
    dialogEdit: false,
    dialogDelete: false,
    dialogDeleteConfirm: false,
    confirmFlag: true,
    deletedItem: null,
    items: [],
    municipalities: [],
    allArea: [],
    areas: [],
    facilities: [],
    editedIndex: -1,
    group_name: "",
    year: "",
    editedItem: {},
    municipality: undefined,
    area: undefined,
    facility: undefined,
    type: "",
    facilityType: null,
    screen: null,
    groupNameFilterValue: null,
    facilityNameFilterValue: null,
    issueDateValue: null,
    rules: {
      required: value => !!value || ErrorTodoMessages.ERWE40004,
      numberCheck: value => !isNaN(value) || ErrorTodoMessages.ERWE40008,
      yearCheck: value =>
        /^[0-9]{4}$/.test(value) || ErrorTodoMessages.ERWE40015(4),
      zenkaku: value => {
        const PATTERN = /^[^!-~｡-ﾟ]+$/;
        return PATTERN.test(value) || ErrorTodoMessages.ERWE40037;
      }
    },
    config: config, // 共通プレースホルダ
    dialogConfirmFlag: false //  ダイアログ（編集）更新ボタンの活性化フラグ
  }),

  computed: {
    qrCodeFormPath() {
      if (this.facilityType.isSchool) {
        return "/qr-code-form/school-qr-code-form.xlsm";
      } else {
        return "/qr-code-form/facility-qr-code-form.xlsm";
      }
    },
    qrCodeFormName() {
      if (this.facilityType.isSchool) {
        return "学校QRコード登録.xlsm";
      } else {
        return "要配慮者施設QRコード登録.xlsm";
      }
    },
    headers() {
      if (this.facilityType.isSchool) {
        return [
          {
            text: "QRコード表示",
            value: "qrCodeActions",
            sortable: false
          },
          {
            text: "発行単位ID",
            align: " d-none",
            value: "manage_grp_id"
          },
          {
            text: "発行単位",
            value: "manage_grp_name"
          },
          {
            text: "施設ID",
            align: " d-none",
            value: "fa_id"
          },
          {
            text: "施設名",
            value: "fa_name"
          },
          {
            text: "ID",
            align: " d-none",
            value: "qr_code_id"
          },
          {
            text: "年度",
            value: "year"
          },
          {
            text: "発行日",
            value: "created_date"
          },
          {
            text: "最終更新日",
            value: "modified_date"
          },
          {
            text: "発行者一覧表示",
            value: "viewActions",
            sortable: false
          },
          {
            text: "発行単位操作",
            value: "actions",
            sortable: false
          },
          {
            text: "ファイル操作",
            value: "fileActions",
            sortable: false
          }
        ];
      } else {
        return [
          {
            text: "QRコード表示",
            value: "qrCodeActions",
            width: "10%",
            sortable: false
          },
          {
            text: "発行単位ID",
            align: " d-none",
            value: "manage_grp_id"
          },
          {
            text: "発行単位",
            value: "manage_grp_name",
            width: "25%"
          },
          {
            text: "施設ID",
            align: " d-none",
            value: "fa_id"
          },
          {
            text: "施設名",
            value: "fa_name",
            width: "25%"
          },
          {
            text: "ID",
            align: " d-none",
            value: "qr_code_id"
          },
          {
            text: "発行日",
            value: "created_date",
            width: "5%"
          },
          {
            text: "最終更新日",
            value: "modified_date",
            width: "10%"
          },
          {
            text: "発行者一覧表示",
            value: "viewActions",
            width: "10%",
            sortable: false
          },
          {
            text: "発行単位操作",
            value: "actions",
            width: "10%",
            sortable: false
          },
          {
            text: "ファイル操作",
            value: "fileActions",
            width: "5%",
            sortable: false
          }
        ];
      }
    },

    selectTypes() {
      // 地域が未選択の場合、施設区分を出さないようにする
      if (this.area === void 0) {
        return [];
      }
      return [
        {
          text: "病院",
          value: "hospital"
        },
        {
          text: "福祉施設",
          value: "welfareFacility"
        },
        {
          text: "避難行動要支援者名簿",
          value: "evacuationList"
        }
      ];
    },

    displayIssueDateValue: {
      get: function() {
        return this.formatDate(this.issueDateValue);
      },
      set: function() {}
    },
    // ErrorMessage.vueに渡す値
    // （要配慮者施設QRまたは学校ＱＲコード発行単位登録画面の場合はメッセージ出しわけ必要なため）
    errorScreenId() {
      if (this.facilityType.isSchool) {
        return "SF0604";
      } else {
        return "SF0601";
      }
    }
  },

  watch: {
    dialogEdit(val) {
      val || this.closeDialogEdit();
    },
    $route: "initialize"
  },

  mounted() {
    this.pageTitle(this.screen.screenTitle);
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      // 読み込みGUI表示
      this.overlay = true;
      this.loading = true;
      // 他項目の初期化処理
      this.items = [];
      this.municipalities = [];
      this.municipality = undefined;
      this.areas = [];
      this.area = undefined;
      this.type = "";
      this.facilities = [];
      this.facility = undefined;
      this.registerValid();

      this.facilityType = this.$facilityType.factory(
        this.$route.name === "school-qr-code-group"
      );

      // 画面情報
      const groupScreenId = this.facilityType.groupScreenId();

      // 画面参照権限のみの場合、新規登録ボタンの非表示のために権限情報取得
      this.screen = this.$screenOperationAuthority(
        groupScreenId,
        groupScreenId
      );

      // 本来は都道府県コードを渡す
      let screenId = "";
      if (this.facilityType.isSchool) {
        screenId = "SF0604";
      } else {
        screenId = "SF0601";
      }
      await allAreaRepository
        .getAllArea()
        .then(response => {
          this.prefectureInfo = response.data.results[0];
          // 市町村情報を抜き出す
          this.municipalities = this.prefectureInfo.municipalities.filter(
            m => m.disp_flg !== "1"
          );
        })
        .catch(e => {
          this.handleError(e, screenId, "aggregation-menu");
        });

      // テーブルの更新
      this.getItems();

      // 読み込みGUI非表示
      this.overlay = false;
      this.loading = false;
    },

    // 市町村変更時
    async changeMunicipality(municipality) {
      // 読み込みGUI表示
      this.overlay = true;
      this.loading = true;
      // 他項目の初期化処理
      this.areas = [];
      this.area = undefined;
      this.type = "";
      this.facilities = [];
      this.facility = undefined;
      this.registerValid();

      if (municipality === void 0) {
        // 読み込みGUI非表示
        this.overlay = false;
        this.loading = false;
        return;
      }

      municipality.cvr_areas.forEach(c_area => {
        Array.prototype.push.apply(this.areas, c_area.areas);
      });

      this.areas = this.areas.filter(area => area.disp_flg === "0");

      // 読み込みGUI非表示
      this.overlay = false;
      this.loading = false;
    },

    // 地域変更時
    async changeArea() {
      // 他項目の初期化処理
      this.type = "";
      this.facilities = [];
      this.facility = undefined;
      this.registerValid();

      // 地域(地区)の入力フォームをｘにした時、APIを叩かないようにする
      if (!this.area?.area_code) {
        return;
      }

      // 学校の場合はココで施設情報を取得
      if (this.facilityType.isSchool) {
        // 読み込みGUI表示
        this.overlay = true;
        this.loading = true;
        // 要配慮者施設のデータ取得
        await facilityRepository
          .getFacility(
            this.municipality.municip_code,
            this.area.area_code,
            ["3"],
            "SF0604"
          )
          .then(response => {
            // typeに応じてデータを抽出
            response.data.results.forEach(ele => {
              ele["3"]["school"].forEach(typeInfo => {
                this.facilities.push({
                  facility_id: typeInfo.fa_id,
                  facility_name: typeInfo.fa_name,
                  disp_no: typeInfo.disp_no
                });
              });
            });
          })
          .catch(e => {
            this.clearMessage();
            this.handleGetError(e);
          });
        // 読み込みGUI非表示
        this.overlay = false;
        this.loading = false;
      }
    },

    // 施設区分変更時
    async changeType(code) {
      // 読み込みGUI表示
      this.overlay = true;
      this.loading = true;
      // 他項目の初期化処理
      this.facilities = [];
      this.facility = undefined;
      this.registerValid();

      if (code === void 0) {
        // 読み込みGUI非表示
        this.overlay = false;
        this.loading = false;
        return;
      }

      //避難行動要支援者名簿の場合、施設名データ「-」
      if (this.type == "evacuationList") {
        this.facilities.push({
          facility_id: "FA999999", //固定id
          facility_name: "-", //固定「-」
          disp_no: ""
        });
      } else {
        // 要配慮者施設のデータ取得
        await facilityRepository
          .getFacility(
            this.municipality.municip_code,
            this.area.area_code,
            ["3"],
            "SF0601"
          )
          .then(response => {
            // typeに応じてデータを抽出
            response.data.results.forEach(ele => {
              ele["3"][this.type].forEach(typeInfo => {
                this.facilities.push({
                  facility_id: typeInfo.fa_id,
                  facility_name: typeInfo.fa_name,
                  disp_no: typeInfo.disp_no
                });
              });
            });
          })
          .catch(e => {
            this.handleGetError(e);
          });
      }

      // 読み込みGUI非表示
      this.overlay = false;
      this.loading = false;
    },

    changeFacility() {
      this.registerValid();
    },

    /**
     * 新規登録ボタン押下時処理
     */
    async executeConfirm() {
      // 読み込みGUI表示
      this.overlay = true;
      this.loading = true;
      if (this.facilityType.isSchool) {
        this.clearMessage();
        await vulFacilityEvagrpRepository
          .registerFacilityEvaGrp(
            {
              manage_grp_name: this.group_name,
              manage_grp_type: "SC",
              fa_id: this.facility.facility_id,
              fa_name: this.facility.facility_name,
              year: this.year,
              municip_code: this.municipality.municip_code,
              municip_name: this.municipality.municip_name,
              municip_name_en: this.municipality.municip_name_en,
              area_code: this.area.area_code,
              area_name: this.area.area_name,
              area_name_en: this.area.area_name_en
            },
            "SF0604"
          )
          .then(response => {
            this.handleSuccess(response);
          })
          .catch(e => {
            this.handleCreateError(e);
          });
      } else if (
        this.type === "welfareFacility" ||
        this.type === "hospital" ||
        this.type === "evacuationList"
      ) {
        this.clearMessage();
        let group_type = this.type === "evacuationList" ? "SU" : "VU";
        await vulFacilityEvagrpRepository
          .registerFacilityEvaGrp(
            {
              manage_grp_name: this.group_name,
              manage_grp_type: group_type,
              fa_id: this.facility.facility_id,
              fa_name: this.facility.facility_name,
              year: "",
              municip_code: this.municipality.municip_code,
              municip_name: this.municipality.municip_name,
              municip_name_en: this.municipality.municip_name_en,
              area_code: this.area.area_code,
              area_name: this.area.area_name,
              area_name_en: this.area.area_name_en
            },
            "SF0601"
          )
          .then(response => {
            this.handleSuccess(response);
          })
          .catch(e => {
            this.handleCreateError(e);
          });
      }

      await this.getItems();

      // 読み込みGUI非表示
      this.overlay = false;
      this.loading = false;
    },

    // テーブルの更新
    async getItems() {
      this.items = [];
      let facilityEvaInfoList = [];

      // 要配慮者施設 or 学校
      if (this.facilityType.isSchool) {
        // 学校のパターン
        await vulFacilityEvagrpRepository
          .getFacilityEvaGrpList("SF0604")
          .then(response => {
            facilityEvaInfoList = response;
            facilityEvaInfoList.data.results.forEach(ele => {
              if (ele.manage_grp_type !== "SC") {
                return;
              }
              this.items.push(ele);
            });
          })
          .catch(e => {
            this.handleError(e, "SF0604", "aggregation-menu");
          });
      } else {
        // 要配慮者施設のパターン
        await vulFacilityEvagrpRepository
          .getFacilityEvaGrpList("SF0601")
          .then(response => {
            facilityEvaInfoList = response;
            facilityEvaInfoList.data.results.forEach(ele => {
              if (
                ele.manage_grp_type !== "VU" &&
                ele.manage_grp_type !== "SU"
              ) {
                return;
              }
              this.items.push(ele);
            });
          })
          .catch(e => {
            this.handleError(e, "SF0601", "aggregation-menu");
          });
      }
    },

    openDialogEdit(item) {
      this.editedItem = {
        ...item,
        new_grp_name: item.manage_grp_name
      };
      this.dialogEdit = true;
    },

    /**
     * 編集ダイアログ（更新）
     */
    async editGrpName() {
      // 読み込みGUI表示
      this.overlay = true;
      this.loading = true;
      let screenId = "";
      if (this.facilityType.isSchool) {
        screenId = "SF0604";
      } else {
        screenId = "SF0601";
      }

      this.clearMessage();
      await vulFacilityEvagrpRepository
        .updateFacilityEvaGrp(
          this.editedItem.manage_grp_id,
          this.editedItem.new_grp_name,
          this.editedItem.year,
          this.editedItem.hash,
          "0", // 削除フラグは立てない
          screenId
        )
        .then(response => {
          this.handleSuccess(response);
        })
        .catch(e => {
          this.handleUpdateError(e);
        });

      this.closeDialogEdit();
      await this.getItems();

      // 読み込みGUI非表示
      this.overlay = false;
      this.loading = false;
    },

    /**
     * 編集ダイアログ（クローズ処理）
     */
    closeDialogEdit() {
      this.dialogEdit = false;
      this.dialogConfirmFlag = false; // ダイアログ（編集）の更新ボタンを活性化
    },

    formatDate(date) {
      const formattedDate = this.$moment(date);
      if (formattedDate.isValid()) {
        return formattedDate.format("YYYY/MM/DD");
      }
      return "";
    },

    register(item) {
      this.$router.push({
        path: this.facilityType.registerPath(
          item.manage_grp_id,
          item.manage_grp_type
        )
      });
    },

    openDelete(item) {
      this.deletedItem = item;
      this.dialogDelete = true;
    },

    closeDelete() {
      this.dialogDelete = false;
    },

    async deleteItem() {
      this.closeDeleteConfirm();

      // 読み込みGUI表示
      this.overlay = true;
      this.loading = true;
      let screenId = "";
      if (this.facilityType.isSchool) {
        screenId = "SF0604";
      } else {
        screenId = "SF0601";
      }

      this.clearMessage();
      await vulFacilityEvagrpRepository
        .updateFacilityEvaGrp(
          this.deletedItem.manage_grp_id,
          this.deletedItem.manage_grp_name,
          this.editedItem.year,
          this.deletedItem.hash,
          "1", // 削除フラグを立てる
          screenId
        )
        .then(response => {
          this.handleSuccess(response);
        })
        .catch(e => {
          this.handleUpdateError(e);
        });

      await this.getItems();

      // 読み込みGUI非表示
      this.overlay = false;
      this.loading = false;
    },

    openDeleteConfirm() {
      this.closeDelete();
      this.dialogDeleteConfirm = true;
    },

    closeDeleteConfirm() {
      this.dialogDeleteConfirm = false;
    },

    async qrCodeView(item) {
      if (this.facilityType.isSchool) {
        let students;
        await studentInfoRepository
          .getStudents(item.manage_grp_id, "SF0604")
          .then(response => {
            students = response.data.results;
          })
          .catch(e => {
            this.clearMessage();
            this.handleGetError(e);
          });

        // 生徒数が0かを確認
        if (students.length == 0) {
          // エラーダイアログの表示
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE40033);
          return;
        }

        // 生徒情報をqrdisp_id順に並び替え
        students.sort(function(a, b) {
          if (parseInt(a.qrdisp_id) < parseInt(b.qrdisp_id)) return -1;
          if (parseInt(a.qrdisp_id) > parseInt(b.qrdisp_id)) return 1;
          return 0;
        });

        // PDF生成
        createSchoolQrCodePdf(students, item);
      } else {
        let considerationPersons;
        await considerationPersonInfoRepository
          .getConsiderationPersons(item.manage_grp_id, "SF0601")
          .then(response => {
            considerationPersons = response.data.results;
          })
          .catch(e => {
            this.clearMessage();
            this.handleGetError(e);
          });

        // 要配慮者数が0かを確認
        if (considerationPersons.length == 0) {
          // エラーダイアログの表示
          await this.$refs.errorDialog.open(
            "要配慮者情報が未登録です。要配慮者情報を登録してから、実行してください。"
          );
          return;
        }

        // 要配慮者情報をqrdisp_id順に並び替え
        considerationPersons.sort(function(a, b) {
          if (parseInt(a.qrdisp_id) < parseInt(b.qrdisp_id)) return -1;
          if (parseInt(a.qrdisp_id) > parseInt(b.qrdisp_id)) return 1;
          return 0;
        });

        // PDF生成
        if (this.facilityType.isSchool) {
          createFacilityQrCodePdf(considerationPersons, item.manage_grp_id);
        } else {
          createFacilityQrCodePdf(considerationPersons, item.manage_grp_name);
        }
      }
    },

    convertCSVtoFaItem(item) {
      this.$router.push({
        path: this.facilityType.issuePath(
          item.manage_grp_id,
          item.manage_grp_type
        )
      });
    },

    /**
     * 入力値全角有無チェック
     */
    checkZenkaku(value) {
      const PATTERN = /^[^!-~｡-ﾟ]+$/;
      return !PATTERN.test(value);
    },

    /**
     * 新規登録ボタン活性化制御
     */
    registerValid() {
      if (
        this.facility === undefined ||
        (this.facilityType.isSchool && !this.yearValid()) ||
        this.group_name === "" ||
        this.checkZenkaku(this.group_name)
      ) {
        this.confirmFlag = true;
      } else {
        this.confirmFlag = false;
      }
    },

    yearValid() {
      return !!this.year && !isNaN(this.year) && /^[0-9]{4}$/.test(this.year);
    },

    groupNameFilter(value) {
      if (!this.groupNameFilterValue) {
        return true;
      }
      return value.includes(this.groupNameFilterValue);
    },

    groupfacilityNameFilter(value) {
      if (!this.facilityNameFilterValue) {
        return true;
      }
      return value.includes(this.facilityNameFilterValue);
    },

    issueDateFilter(value) {
      if (!this.issueDateValue) {
        // 空欄の場合はフィルター対象外
        return true;
      }
      let formattedIssueDateValue = this.$moment(this.issueDateValue);
      if (formattedIssueDateValue.isValid()) {
        formattedIssueDateValue = formattedIssueDateValue.format("YYYYMMDD");
      }
      let formattedValue = this.$moment(value);
      if (formattedValue.isValid()) {
        formattedValue = formattedValue.format("YYYYMMDD");
      }
      return formattedIssueDateValue === formattedValue;
    },

    /**
     * ダイアログ（編集）の更新ボタン活性化制御
     */
    dialogValidation(value) {
      this.dialogConfirmFlag = this.checkZenkaku(value);
    },

    // 権限があるかチェック
    hasEditAuth() {
      let screenId = "";
      if (this.facilityType.isSchool) {
        screenId = "SF0604";
      } else {
        screenId = "SF0601";
      }
      const editAuth = this.$screenAuthority.find(
        auth => auth.screen_id === screenId
      );
      if (editAuth) {
        const screenCrud = ScreenCrud.factory(editAuth.screen_crud);
        return screenCrud.isOperation();
      } else {
        return false;
      }
    }
  }
};
</script>
